import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Wallet, Plus, Clock, CheckCircle, XCircle, DollarSign, AlertCircle, Send, History, Trash2, MinusCircle, Users, Eye, Search, Download, Upload, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CashAdvance = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const username = user?.username?.toLowerCase() || '';
  // Only Arbe and Pabs can approve (Alma CANNOT approve)
  const isApprover = ['arbe', 'pabs'].includes(username) || isAdmin;
  // Alma, Arbe, Pabs and Admin can release
  const isReleaser = ['alma', 'arbe', 'pabs'].includes(username) || isAdmin;
  // Admin, Approvers (Arbe, Pabs), and Releaser (Alma) can view All Advances AND Import/Export
  const canViewAllAdvances = isAdmin || isApprover || isReleaser;
  // ALL staff can view "My Requests" tab to see their own requests
  const canViewMyRequests = true;
  
  const [myAdvances, setMyAdvances] = useState([]);
  const [pendingAdvances, setPendingAdvances] = useState([]);
  const [approvedAdvances, setApprovedAdvances] = useState([]);
  const [allAdvances, setAllAdvances] = useState([]);
  const [employeeSummary, setEmployeeSummary] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [caLimit, setCaLimit] = useState(5000);
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showHistoricalDialog, setShowHistoricalDialog] = useState(false);
  const [showEmployeeHistoryDialog, setShowEmployeeHistoryDialog] = useState(false);
  const [employeeHistory, setEmployeeHistory] = useState(null);
  const [employeeHistoryLoading, setEmployeeHistoryLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  
  // My CA Dashboard (for employees to see their balance and deduction history)
  const [myDashboard, setMyDashboard] = useState(null);
  const [releaseHistory, setReleaseHistory] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Edit CA state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCA, setEditingCA] = useState(null);
  const [editFormData, setEditFormData] = useState({
    amount: '',
    reason: '',
    status: '',
    amount_paid: '',
    remarks: ''
  });
  
  // Default tab: 'balance' for all employees to see their CA dashboard first
  const getDefaultTab = () => {
    return 'balance';  // Everyone sees their balance first
  };
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [staffList, setStaffList] = useState([]);
  const [showDeductionDialog, setShowDeductionDialog] = useState(false);
  const [selectedCA, setSelectedCA] = useState(null);
  const [deductionAmount, setDeductionAmount] = useState('');
  const [deductionNotes, setDeductionNotes] = useState('');
  
  // Approval/Rejection dialog state
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [approvalCA, setApprovalCA] = useState(null);
  const [approvalAmount, setApprovalAmount] = useState('');
  const [approvalReason, setApprovalReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalEmployeeHistory, setApprovalEmployeeHistory] = useState(null);
  
  const [requestForm, setRequestForm] = useState({
    amount: '',
    reason: '',
    installments: '1',
    date_required: ''
  });
  const [newLimit, setNewLimit] = useState('');
  const [historicalForm, setHistoricalForm] = useState({
    user_id: '',
    amount: '',
    reason: '',
    installments: '1',
    amount_paid: '0',
    released_at: ''
  });

  useEffect(() => {
    fetchData();
    fetchMyDashboard();
    if (canViewAllAdvances) {
      fetchStaffList();
      fetchEmployeeSummary();
      fetchReleaseHistory();
    }
  }, []);

  const fetchStaffList = async () => {
    try {
      // Try full /users first (admin only). If that fails (e.g. Alma is
      // logged in as staff), fall back to the lightweight directory
      // endpoint so the employee search still works for approvers.
      let list = [];
      try {
        const res = await axios.get(`${API}/users`);
        list = res.data || [];
      } catch (e) {
        if (e.response?.status === 403) {
          const res = await axios.get(`${API}/users/directory`);
          list = res.data || [];
        } else {
          throw e;
        }
      }
      setStaffList(list);
    } catch (error) {
      console.error('Error fetching staff list:', error);
    }
  };

  const fetchEmployeeSummary = async () => {
    try {
      const res = await axios.get(`${API}/cash-advance/employee-summary`);
      setEmployeeSummary(res.data || []);
    } catch (error) {
      console.error('Error fetching employee summary:', error);
    }
  };

  // Fetch employee's own CA dashboard with balance and deduction history
  const fetchMyDashboard = async () => {
    try {
      const res = await axios.get(`${API}/cash-advance/my-dashboard`);
      setMyDashboard(res.data);
    } catch (error) {
      console.error('Error fetching my CA dashboard:', error);
    }
  };

  // Fetch release history for admins/approvers
  const fetchReleaseHistory = async () => {
    try {
      const res = await axios.get(`${API}/cash-advance/release-history`);
      setReleaseHistory(res.data || []);
    } catch (error) {
      console.error('Error fetching release history:', error);
    }
  };

  // Sync CA records (admin only)
  const handleSyncRecords = async () => {
    if (!window.confirm('This will sync all CA records to ensure data consistency. Continue?')) {
      return;
    }
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/cash-advance/sync-records`);
      toast.success(res.data.message);
      // Refresh all data
      fetchData();
      fetchEmployeeSummary();
      fetchReleaseHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync records');
    } finally {
      setSyncing(false);
    }
  };

  const fetchEmployeeHistory = async (userId, forApproval = false) => {
    if (forApproval) {
      try {
        const res = await axios.get(`${API}/cash-advance/user/${userId}/history`);
        setApprovalEmployeeHistory(res.data);
      } catch (error) {
        console.error('Error fetching employee history for approval:', error);
        setApprovalEmployeeHistory(null);
      }
    } else {
      setEmployeeHistoryLoading(true);
      try {
        const res = await axios.get(`${API}/cash-advance/user/${userId}/history`);
        setEmployeeHistory(res.data);
        setShowEmployeeHistoryDialog(true);
        setShowSearchResults(false);
        setSearchQuery('');
      } catch (error) {
        console.error('Error fetching employee history:', error);
        toast.error('Failed to load employee history');
      } finally {
        setEmployeeHistoryLoading(false);
      }
    }
  };

  // Open edit dialog for a cash advance
  const openEditDialog = (ca) => {
    setEditingCA(ca);
    setEditFormData({
      amount: ca.amount || '',
      reason: ca.reason || '',
      status: ca.status || 'pending',
      amount_paid: ca.amount_paid || 0,
      remarks: ca.remarks || ''
    });
    setShowEditDialog(true);
  };

  // Save edited cash advance
  const handleSaveEdit = async () => {
    if (!editingCA) return;
    
    // Store the user_id before closing
    const userId = editingCA.user_id;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/cash-advance/${editingCA.id}`, {
        amount: parseFloat(editFormData.amount),
        reason: editFormData.reason,
        status: editFormData.status,
        amount_paid: parseFloat(editFormData.amount_paid) || 0,
        remarks: editFormData.remarks
      });
      toast.success('Cash advance updated successfully');
      setShowEditDialog(false);
      setEditingCA(null);
      
      // Refresh data
      fetchData();
      if (canViewAllAdvances) fetchEmployeeSummary();
      
      // Refresh employee history if dialog was open
      if (userId) {
        // Small delay to ensure the update is processed
        setTimeout(() => {
          fetchEmployeeHistory(userId);
        }, 300);
      }
    } catch (error) {
      console.error('Error updating cash advance:', error);
      toast.error(error.response?.data?.detail || 'Failed to update cash advance');
    } finally {
      setSubmitting(false);
    }
  };

  // Search employees by name
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    const queryLower = query.toLowerCase();
    // Search in staff list and employee summary
    const matchedStaff = staffList.filter(staff => 
      (staff.full_name || staff.username || '').toLowerCase().includes(queryLower)
    );
    
    // Also include info from employee summary if available
    const results = matchedStaff.map(staff => {
      const summary = employeeSummary.find(emp => emp.user_id === staff.id);
      return {
        ...staff,
        total_outstanding: summary?.total_outstanding || 0,
        pending_amount: summary?.pending_amount || 0,
        hasCA: !!summary
      };
    });
    
    setSearchResults(results);
    setShowSearchResults(true);
  };

  // Export CA data to Excel file
  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/cash-advance/export`, {
        responseType: 'blob'
      });
      
      // Create and download file
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cash_advance_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Cash advance data exported to Excel');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export data');
    }
  };

  // Download import template
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/cash-advance/template`, {
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cash_advance_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Handle file selection for import
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }
    
    if (!window.confirm(`Import cash advances from "${file.name}"? New records will be added.`)) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/cash-advance/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(res.data.message);
      
      if (res.data.errors && res.data.errors.length > 0) {
        toast.warning(`Some rows had issues: ${res.data.errors.slice(0, 3).join(', ')}`);
      }
      
      setShowImportDialog(false);
      fetchData();
      if (canViewAllAdvances) fetchEmployeeSummary();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import data');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const fetchData = async () => {
    try {
      // Get my advances
      const myRes = await axios.get(`${API}/cash-advance/my-requests`);
      setMyAdvances(myRes.data.cash_advances || []);
      setTotalOutstanding(myRes.data.total_outstanding || 0);
      
      // Get settings
      const settingsRes = await axios.get(`${API}/cash-advance/settings`);
      setCaLimit(settingsRes.data.limit || 5000);
      setNewLimit(settingsRes.data.limit || 5000);
      
      // Get pending (if approver)
      if (isApprover) {
        try {
          const pendingRes = await axios.get(`${API}/cash-advance/pending`);
          setPendingAdvances(pendingRes.data || []);
        } catch (e) {}
      }
      
      // Get approved (if releaser)
      if (isReleaser) {
        try {
          const approvedRes = await axios.get(`${API}/cash-advance/approved`);
          setApprovedAdvances(approvedRes.data || []);
        } catch (e) {}
      }
      
      // Get all (if admin, approver, or releaser/Alma)
      if (canViewAllAdvances) {
        try {
          const allRes = await axios.get(`${API}/cash-advance/all`);
          setAllAdvances(allRes.data || []);
        } catch (e) {}
      }
      
      // Refresh my dashboard
      fetchMyDashboard();
      
      // Refresh release history for admins
      if (canViewAllAdvances) {
        fetchReleaseHistory();
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load cash advance data');
      setLoading(false);
    }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.amount || !requestForm.reason || !requestForm.date_required) {
      toast.error('Please fill in amount, reason, and date required');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/cash-advance/request`, {
        amount: parseFloat(requestForm.amount),
        reason: requestForm.reason,
        installments: parseInt(requestForm.installments),
        date_required: requestForm.date_required
      });
      toast.success('Cash advance request submitted!');
      setShowRequestDialog(false);
      setRequestForm({ amount: '', reason: '', installments: '1', date_required: '' });
      fetchData();
      if (canViewAllAdvances) fetchEmployeeSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (caId) => {
    // Find the CA to show in dialog
    const ca = [...pendingAdvances, ...allAdvances].find(c => c.id === caId);
    if (ca) {
      setApprovalCA(ca);
      setApprovalAmount(ca.amount.toString());
      setApprovalReason('');
      setApprovalEmployeeHistory(null);
      setShowApprovalDialog(true);
      // Fetch employee history for approval dialog
      fetchEmployeeHistory(ca.user_id, true);
    }
  };

  const submitApproval = async () => {
    if (!approvalCA) return;
    
    try {
      const payload = {};
      const approvedAmt = parseFloat(approvalAmount);
      
      // Check if it's partial approval
      if (approvedAmt !== approvalCA.amount) {
        payload.approved_amount = approvedAmt;
      }
      if (approvalReason.trim()) {
        payload.reason = approvalReason.trim();
      }
      
      await axios.put(`${API}/cash-advance/${approvalCA.id}/approve`, 
        Object.keys(payload).length > 0 ? payload : null
      );
      
      if (approvedAmt !== approvalCA.amount) {
        toast.success(`Cash advance partially approved: ₱${approvedAmt.toLocaleString()}`);
      } else {
        toast.success('Cash advance approved!');
      }
      setShowApprovalDialog(false);
      setApprovalCA(null);
      setApprovalEmployeeHistory(null);
      fetchData();
      if (canViewAllAdvances) fetchEmployeeSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRelease = async (caId) => {
    try {
      await axios.put(`${API}/cash-advance/${caId}/release`);
      toast.success('Cash advance released!');
      fetchData();
      if (canViewAllAdvances) fetchEmployeeSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release');
    }
  };

  const handleReject = async (caId) => {
    const ca = [...pendingAdvances, ...allAdvances].find(c => c.id === caId);
    if (ca) {
      setApprovalCA(ca);
      setRejectionReason('');
      setShowRejectionDialog(true);
    }
  };

  const submitRejection = async () => {
    if (!approvalCA) return;
    if (!rejectionReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }
    
    try {
      await axios.put(`${API}/cash-advance/${approvalCA.id}/reject`, {
        reason: rejectionReason.trim()
      });
      toast.success('Cash advance rejected');
      setShowRejectionDialog(false);
      setApprovalCA(null);
      setRejectionReason('');
      fetchData();
      if (canViewAllAdvances) fetchEmployeeSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleUpdateLimit = async () => {
    try {
      await axios.put(`${API}/cash-advance/settings?limit=${newLimit}`);
      toast.success('CA limit updated!');
      setCaLimit(parseFloat(newLimit));
      setShowSettingsDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update limit');
    }
  };

  const handleHistoricalSubmit = async (e) => {
    e.preventDefault();
    if (!historicalForm.user_id || !historicalForm.amount || !historicalForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/cash-advance/add-historical`, {
        user_id: historicalForm.user_id,
        amount: parseFloat(historicalForm.amount),
        reason: historicalForm.reason,
        installments: parseInt(historicalForm.installments),
        amount_paid: parseFloat(historicalForm.amount_paid || 0),
        released_at: historicalForm.released_at || null
      });
      toast.success('Historical cash advance added!');
      setShowHistoricalDialog(false);
      setHistoricalForm({
        user_id: '',
        amount: '',
        reason: '',
        installments: '1',
        amount_paid: '0',
        released_at: ''
      });
      fetchData();
      if (canViewAllAdvances) fetchEmployeeSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add historical CA');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCA = async (caId, userName) => {
    if (!window.confirm(`Are you sure you want to delete the cash advance for ${userName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/cash-advance/${caId}`);
      toast.success('Cash advance deleted successfully');
      fetchData();
      if (canViewAllAdvances) fetchEmployeeSummary();
      
      // Refresh employee history if dialog is open
      if (employeeHistory && employeeHistory.user_id) {
        fetchEmployeeHistory(employeeHistory.user_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete cash advance');
    }
  };

  const openDeductionDialog = (ca) => {
    setSelectedCA(ca);
    setDeductionAmount('');
    setDeductionNotes('');
    setShowDeductionDialog(true);
  };

  const handleRecordDeduction = async (e) => {
    e.preventDefault();
    if (!deductionAmount || parseFloat(deductionAmount) <= 0) {
      toast.error('Please enter a valid deduction amount');
      return;
    }
    
    if (parseFloat(deductionAmount) > selectedCA.amount_remaining) {
      toast.error(`Amount exceeds remaining balance (${formatCurrency(selectedCA.amount_remaining)})`);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/cash-advance/${selectedCA.id}/manual-deduction`, {
        amount: parseFloat(deductionAmount),
        notes: deductionNotes || 'Manual deduction'
      });
      toast.success('Deduction recorded successfully!');
      setShowDeductionDialog(false);
      setSelectedCA(null);
      fetchData();
      if (canViewAllAdvances) fetchEmployeeSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record deduction');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      approved: 'bg-blue-100 text-blue-700 border-blue-200',
      released: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      completed: 'bg-slate-100 text-slate-700 border-slate-200',
      rejected: 'bg-red-100 text-red-700 border-red-200'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  const availableCA = caLimit - totalOutstanding;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-advance-page">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Cash Advance</h2>
          <p className="text-slate-600 mt-1">Request and manage cash advances</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canViewAllAdvances && (
            <>
              <Button 
                variant="outline"
                onClick={handleExport}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                data-testid="export-ca-btn"
              >
                <Download size={18} className="mr-2" />
                Export
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowImportDialog(true)}
                className="text-green-600 border-green-200 hover:bg-green-50"
                data-testid="import-ca-btn"
              >
                <Upload size={18} className="mr-2" />
                Import
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowHistoricalDialog(true)}
                className="bg-blue-50 border-blue-200 hover:bg-blue-100"
                data-testid="add-historical-ca-btn"
              >
                <Plus size={18} className="mr-2" />
                Add CA for Employee
              </Button>
              {isAdmin && (
                <Button 
                  variant="outline"
                  onClick={handleSyncRecords}
                  disabled={syncing}
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  data-testid="sync-ca-btn"
                >
                  {syncing ? (
                    <span className="animate-spin mr-2">⏳</span>
                  ) : (
                    <History size={18} className="mr-2" />
                  )}
                  Sync Records
                </Button>
              )}
              <Button 
                variant="outline"
                onClick={() => setShowSettingsDialog(true)}
              >
                Settings
              </Button>
            </>
          )}
          <Button 
            onClick={() => setShowRequestDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus size={18} className="mr-2" />
            Request CA
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Wallet className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-blue-600">My Outstanding CA</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(totalOutstanding)}</p>
            </div>
          </div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <DollarSign className="text-emerald-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-emerald-600">Available to Borrow</p>
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(availableCA)}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <AlertCircle className="text-slate-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-600">CA Limit</p>
              <p className="text-2xl font-bold text-slate-700">{formatCurrency(caLimit)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar - For Admins/Approvers/Releasers */}
      {canViewAllAdvances && (
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                type="text"
                placeholder="Search employee by name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                className="pl-10 pr-4"
                data-testid="ca-search-input"
              />
            </div>
            {searchQuery && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  setShowSearchResults(false);
                }}
              >
                Clear
              </Button>
            )}
          </div>
          
          {/* Search Results Dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute z-50 mt-1 w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
              <div className="p-2 border-b border-slate-100 bg-slate-50">
                <p className="text-xs text-slate-500">
                  Found {searchResults.length} employee(s)
                </p>
              </div>
              {searchResults.map((staff) => (
                <button
                  key={staff.id}
                  onClick={() => fetchEmployeeHistory(staff.id)}
                  className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-slate-100 last:border-0 flex justify-between items-center"
                  data-testid={`search-result-${staff.id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900">{staff.full_name || staff.username}</p>
                    <p className="text-xs text-slate-500">@{staff.username}</p>
                  </div>
                  <div className="text-right">
                    {staff.hasCA ? (
                      <>
                        <p className="text-sm font-medium text-amber-600">
                          {formatCurrency(staff.total_outstanding)}
                        </p>
                        {staff.pending_amount > 0 && (
                          <p className="text-xs text-blue-600">
                            +{formatCurrency(staff.pending_amount)} pending
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-slate-400">No CA records</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && (
            <div className="absolute z-50 mt-1 w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-lg p-4 text-center text-slate-500">
              No employees found matching "{searchQuery}"
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-4 flex-wrap">
          {/* My Balance Tab - For employees to see their CA balance and deduction history */}
          <button
            onClick={() => setActiveTab('balance')}
            className={`py-2 px-4 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'balance' 
                ? 'border-emerald-600 text-emerald-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="my-balance-tab"
          >
            <DollarSign size={16} />
            My Balance
            {myDashboard?.summary?.total_outstanding > 0 && (
              <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {formatCurrency(myDashboard.summary.total_outstanding)}
              </span>
            )}
          </button>
          {/* Manage All Tab - For Admin - This is the main management view */}
          {canViewAllAdvances && (
            <button
              onClick={() => setActiveTab('all')}
              className={`py-2 px-4 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'all' 
                  ? 'border-emerald-600 text-emerald-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="manage-all-tab"
            >
              <Wallet size={16} />
              Manage All
              {allAdvances.length > 0 && (
                <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {allAdvances.length}
                </span>
              )}
            </button>
          )}
          {/* Release History Tab - for Arbe, Pabs, Alma */}
          {canViewAllAdvances && (
            <button
              onClick={() => setActiveTab('release-history')}
              className={`py-2 px-4 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'release-history' 
                  ? 'border-green-600 text-green-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="release-history-tab"
            >
              <History size={16} />
              Release History
              {releaseHistory.length > 0 && (
                <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {releaseHistory.length}
                </span>
              )}
            </button>
          )}
          {/* Employee Summary Tab - for admins, approvers, and Alma */}
          {canViewAllAdvances && (
            <button
              onClick={() => setActiveTab('employees')}
              className={`py-2 px-4 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'employees' 
                  ? 'border-purple-600 text-purple-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="employees-tab"
            >
              <Users size={16} />
              By Employee
              {employeeSummary.length > 0 && (
                <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {employeeSummary.length}
                </span>
              )}
            </button>
          )}
          {isApprover && (
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-2 px-4 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'pending' 
                  ? 'border-amber-600 text-amber-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="pending-approval-tab"
            >
              <Clock size={16} />
              Pending Approval
              {pendingAdvances.length > 0 && (
                <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingAdvances.length}
                </span>
              )}
            </button>
          )}
          {isReleaser && (
            <button
              onClick={() => setActiveTab('approved')}
              className={`py-2 px-4 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'approved' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="for-release-tab"
            >
              <Send size={16} />
              For Release
              {approvedAdvances.length > 0 && (
                <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {approvedAdvances.length}
                </span>
              )}
            </button>
          )}
          {/* My Requests tab - visible to everyone (shows their own requests) */}
          {canViewMyRequests && (
            <button
              onClick={() => setActiveTab('my')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'my' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="my-requests-tab"
            >
              My Requests
            </button>
          )}
          {canViewAllAdvances && (
            <button
              onClick={() => setActiveTab('rejected')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'rejected' 
                  ? 'border-red-600 text-red-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid="rejected-tab"
            >
              Rejected
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* My Balance Tab - Shows employee's CA balance and deduction history */}
        {activeTab === 'balance' && (
          <div className="p-6">
            {/* Balance Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-600">Total Borrowed</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(myDashboard?.summary?.total_borrowed || 0)}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="text-sm text-emerald-600">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(myDashboard?.summary?.total_paid || 0)}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-600">Outstanding Balance</p>
                <p className="text-2xl font-bold text-amber-700">{formatCurrency(myDashboard?.summary?.total_outstanding || 0)}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-sm text-slate-600">Pending Requests</p>
                <p className="text-2xl font-bold text-slate-700">{formatCurrency(myDashboard?.summary?.pending_requests || 0)}</p>
              </div>
            </div>

            {/* Active Loans */}
            {myDashboard?.active_loans?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Active Loans</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-amber-50 border-b border-amber-200">
                      <tr>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-amber-700 uppercase">Released Date</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-amber-700 uppercase">Reason</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-amber-700 uppercase">Original Amount</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-amber-700 uppercase">Paid</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-amber-700 uppercase">Remaining</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      {myDashboard.active_loans.map((loan) => (
                        <tr key={loan.id} className="hover:bg-amber-50">
                          <td className="px-4 py-3 text-sm text-slate-700">{formatDateShort(loan.released_at)}</td>
                          <td className="px-4 py-3 text-sm text-slate-700">{loan.reason}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-slate-900">{formatCurrency(loan.amount)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-emerald-600">{formatCurrency(loan.amount_paid || 0)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-amber-600">{formatCurrency(loan.amount_remaining)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Deduction History */}
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Deduction History</h3>
              {myDashboard?.deduction_history?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-emerald-50 border-b border-emerald-200">
                      <tr>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-emerald-700 uppercase">Date</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-emerald-700 uppercase">Payslip Period</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-emerald-700 uppercase">CA Reason</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-emerald-700 uppercase">Amount Deducted</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-emerald-700 uppercase">Deducted By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-emerald-100">
                      {myDashboard.deduction_history.map((ded, idx) => (
                        <tr key={idx} className="hover:bg-emerald-50">
                          <td className="px-4 py-3 text-sm text-slate-700">{formatDateShort(ded.deducted_at)}</td>
                          <td className="px-4 py-3 text-sm">
                            {ded.payslip_label ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                📄 {ded.payslip_label}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{ded.ca_reason || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-emerald-600">{formatCurrency(ded.deduction_amount)}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{ded.deducted_by}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  No deductions recorded yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* Release History Tab - For Arbe, Pabs, Alma */}
        {activeTab === 'release-history' && canViewAllAdvances && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-green-50 border-b border-green-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-green-700 uppercase">Date Released</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-green-700 uppercase">Employee</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-green-700 uppercase">Amount</th>
                  <th className="text-center px-6 py-3 text-xs font-semibold text-green-700 uppercase">Installments</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-green-700 uppercase">Released By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-green-100">
                {releaseHistory.length > 0 ? releaseHistory.map((log) => (
                  <tr key={log.id} className="hover:bg-green-50">
                    <td className="px-6 py-4 text-sm text-slate-700">{formatDate(log.released_at)}</td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-slate-900">{log.employee_name}</span>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-green-600">{formatCurrency(log.amount)}</td>
                    <td className="px-6 py-4 text-center">
                      {log.installments > 1 ? (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {log.installments}x @ {formatCurrency(log.amount_per_installment)}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-purple-600 font-medium">{log.released_by}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      No release history found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* My Requests Tab */}
        {activeTab === 'my' && canViewMyRequests && (
          <div className="overflow-x-auto">
            {/* Show pending requests alert if any */}
            {myAdvances.filter(ca => ca.status === 'pending').length > 0 && (
              <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
                <div className="flex items-center gap-2 text-amber-700">
                  <Clock size={18} />
                  <span className="font-medium">
                    You have {myAdvances.filter(ca => ca.status === 'pending').length} pending request(s) awaiting approval
                  </span>
                </div>
              </div>
            )}
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Reason</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Installments</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Paid / Remaining</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {myAdvances.length > 0 ? myAdvances.map((ca) => (
                  <tr key={ca.id} className={`hover:bg-slate-50 ${ca.status === 'rejected' ? 'bg-red-50' : ''} ${ca.status === 'pending' ? 'bg-amber-50' : ''}`}>
                    <td className="px-6 py-4 text-slate-700">{formatDate(ca.requested_at)}</td>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      {formatCurrency(ca.amount)}
                      {ca.original_requested_amount && ca.original_requested_amount !== ca.amount && (
                        <p className="text-xs text-amber-600">
                          (Requested: {formatCurrency(ca.original_requested_amount)})
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {ca.reason}
                      {ca.status === 'pending' && (
                        <p className="text-xs text-amber-600 mt-1 font-medium">
                          ⏳ Waiting for approval
                        </p>
                      )}
                      {ca.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">
                          <strong>Rejected:</strong> {ca.rejection_reason}
                        </p>
                      )}
                      {ca.approval_note && (
                        <p className="text-xs text-blue-600 mt-1">
                          <strong>Note:</strong> {ca.approval_note}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {ca.installments}x @ {formatCurrency(ca.amount_per_installment || 0)}/cutoff
                    </td>
                    <td className="px-6 py-4">
                      {ca.status === 'released' || ca.status === 'completed' ? (
                        <div>
                          <span className="text-emerald-600 font-medium">
                            {formatCurrency(ca.amount_paid || 0)}
                          </span>
                          <span className="text-slate-400 mx-1">/</span>
                          <span className={ca.amount_remaining > 0 ? 'text-amber-600 font-medium' : 'text-slate-500'}>
                            {formatCurrency(ca.amount_remaining || 0)}
                          </span>
                        </div>
                      ) : ca.status === 'pending' ? (
                        <span className="text-amber-500 text-sm">Awaiting approval</span>
                      ) : ca.status === 'approved' ? (
                        <span className="text-blue-500 text-sm">Approved - awaiting release</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(ca.status)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                      No cash advance requests yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Employee Summary Tab */}
        {activeTab === 'employees' && canViewAllAdvances && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-purple-50 border-b border-purple-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Employee</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Outstanding</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Pending Approval</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Total (Outstanding + Pending)</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Last Request</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employeeSummary.length > 0 ? employeeSummary.map((emp) => (
                  <tr key={emp.user_id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => fetchEmployeeHistory(emp.user_id)}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        data-testid={`employee-name-${emp.user_id}`}
                      >
                        {emp.user_name}
                      </button>
                      <p className="text-xs text-slate-500">
                        {emp.active_count} active CA{emp.active_count !== 1 ? 's' : ''}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-semibold text-amber-600">
                      {formatCurrency(emp.total_outstanding)}
                    </td>
                    <td className="px-6 py-4">
                      {emp.pending_count > 0 ? (
                        <div>
                          <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                            {emp.pending_count} pending
                          </span>
                          <p className="text-sm text-amber-600 mt-1 font-medium">
                            {formatCurrency(emp.pending_amount || 0)}
                          </p>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-bold text-red-600">
                      {formatCurrency((emp.total_outstanding || 0) + (emp.pending_amount || 0))}
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-sm">
                      {formatDateShort(emp.last_request_date)}
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fetchEmployeeHistory(emp.user_id)}
                        data-testid={`view-history-${emp.user_id}`}
                      >
                        <Eye size={14} className="mr-1" />
                        View History
                      </Button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                      No employees with cash advances
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pending Approval Tab */}
        {activeTab === 'pending' && isApprover && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Staff</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Reason</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date Required</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Outstanding</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Installments</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pendingAdvances.length > 0 ? pendingAdvances.map((ca) => (
                  <tr key={ca.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => fetchEmployeeHistory(ca.user_id)}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      >
                        {ca.user_name}
                      </button>
                      <p className="text-xs text-slate-500">{formatDate(ca.requested_at)}</p>
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900">{formatCurrency(ca.amount)}</td>
                    <td className="px-6 py-4 text-slate-700">{ca.reason}</td>
                    <td className="px-6 py-4 text-slate-700">
                      {ca.date_required ? formatDateShort(ca.date_required) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={ca.user_total_outstanding > 0 ? 'text-amber-600 font-medium' : 'text-slate-500'}>
                        {formatCurrency(ca.user_total_outstanding)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-700">{ca.installments}x</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApprove(ca.id)}
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleReject(ca.id)}
                        >
                          <XCircle size={16} className="mr-1" />
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                      No pending requests
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* For Release Tab */}
        {activeTab === 'approved' && isReleaser && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Staff</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Approved By</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Approved At</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {approvedAdvances.length > 0 ? approvedAdvances.map((ca) => (
                  <tr key={ca.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">{ca.user_name}</td>
                    <td className="px-6 py-4 font-semibold text-slate-900">{formatCurrency(ca.amount)}</td>
                    <td className="px-6 py-4 text-slate-700">{ca.approved_by}</td>
                    <td className="px-6 py-4 text-slate-700">{formatDate(ca.approved_at)}</td>
                    <td className="px-6 py-4">
                      <Button 
                        size="sm" 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleRelease(ca.id)}
                      >
                        <Send size={16} className="mr-1" />
                        Release Money
                      </Button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      No approved requests waiting for release
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* All Advances Tab (Admin) */}
        {activeTab === 'all' && canViewAllAdvances && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Staff</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date Required</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Paid</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Remaining</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allAdvances.length > 0 ? allAdvances.map((ca) => (
                  <tr key={ca.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => fetchEmployeeHistory(ca.user_id)}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      >
                        {ca.user_name}
                      </button>
                      <p className="text-xs text-slate-500">{ca.reason}</p>
                    </td>
                    <td className="px-6 py-4 font-semibold text-slate-900">{formatCurrency(ca.amount)}</td>
                    <td className="px-6 py-4 text-slate-700">
                      {ca.date_required ? formatDateShort(ca.date_required) : '-'}
                    </td>
                    <td className="px-6 py-4 text-emerald-600 font-medium">{formatCurrency(ca.amount_paid || 0)}</td>
                    <td className="px-6 py-4 text-amber-600 font-medium">{formatCurrency(ca.amount_remaining)}</td>
                    <td className="px-6 py-4">{getStatusBadge(ca.status)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-slate-600 border-slate-200 hover:bg-slate-50"
                          onClick={() => openEditDialog(ca)}
                          data-testid={`edit-btn-${ca.id}`}
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                        {ca.status === 'released' && ca.amount_remaining > 0 && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => openDeductionDialog(ca)}
                            data-testid={`deduct-btn-${ca.id}`}
                          >
                            <MinusCircle size={14} className="mr-1" />
                            Deduct
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleDeleteCA(ca.id, ca.user_name)}
                          data-testid={`delete-btn-${ca.id}`}
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                      No cash advances
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Rejected Tab (Admin) */}
        {activeTab === 'rejected' && canViewAllAdvances && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-red-50 border-b border-red-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Staff</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Request Reason</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Rejection Reason</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Rejected By</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Rejected Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allAdvances.filter(ca => ca.status === 'rejected').length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-slate-500">
                      No rejected cash advances
                    </td>
                  </tr>
                ) : (
                  allAdvances.filter(ca => ca.status === 'rejected').map((ca) => (
                    <tr key={ca.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(ca.requested_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {ca.user_name}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {formatCurrency(ca.original_requested_amount || ca.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate" title={ca.reason}>
                        {ca.reason}
                      </td>
                      <td className="px-6 py-4 text-sm text-red-600 font-medium max-w-xs">
                        {ca.rejection_reason || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {ca.rejected_by || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {ca.rejected_at ? new Date(ca.rejected_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Request CA Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Cash Advance</DialogTitle>
            <DialogDescription>
              Available: {formatCurrency(availableCA)} (Limit: {formatCurrency(caLimit)})
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRequest} className="space-y-4">
            <div>
              <Label htmlFor="amount">Amount (₱) *</Label>
              <Input
                id="amount"
                type="number"
                value={requestForm.amount}
                onChange={(e) => setRequestForm({ ...requestForm, amount: e.target.value })}
                placeholder="e.g., 1000"
                min="100"
                max={availableCA}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="reason">Reason *</Label>
              <textarea
                id="reason"
                value={requestForm.reason}
                onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                placeholder="Why do you need this cash advance?"
                required
                rows="2"
                className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <Label htmlFor="date_required">Date Required *</Label>
              <Input
                id="date_required"
                type="date"
                value={requestForm.date_required}
                onChange={(e) => setRequestForm({ ...requestForm, date_required: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="installments">Number of Installments</Label>
              <Select
                value={requestForm.installments}
                onValueChange={(value) => setRequestForm({ ...requestForm, installments: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 (Full deduction on next payslip)</SelectItem>
                  <SelectItem value="2">2 payslips</SelectItem>
                  <SelectItem value="3">3 payslips</SelectItem>
                  <SelectItem value="4">4 payslips</SelectItem>
                  <SelectItem value="6">6 payslips</SelectItem>
                </SelectContent>
              </Select>
              {requestForm.amount && (
                <p className="text-sm text-slate-500 mt-1">
                  Deduction per payslip: {formatCurrency(parseFloat(requestForm.amount) / parseInt(requestForm.installments || 1))}
                </p>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowRequestDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog (Admin) */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cash Advance Settings</DialogTitle>
            <DialogDescription>
              Configure cash advance limits for employees
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="limit">CA Limit per Employee (₱)</Label>
              <Input
                id="limit"
                type="number"
                value={newLimit}
                onChange={(e) => setNewLimit(e.target.value)}
                min="0"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Maximum total outstanding CA an employee can have
              </p>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowSettingsDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateLimit} className="bg-blue-600 hover:bg-blue-700">
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Historical CA Dialog (Admin) */}
      <Dialog open={showHistoricalDialog} onOpenChange={setShowHistoricalDialog}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Existing Cash Advance</DialogTitle>
            <DialogDescription>
              Add a pre-existing or historical cash advance for tracking purposes
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleHistoricalSubmit} className="space-y-4">
            <div>
              <Label htmlFor="hist_user">Staff Member *</Label>
              <Select
                value={historicalForm.user_id}
                onValueChange={(value) => setHistoricalForm({ ...historicalForm, user_id: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.full_name || staff.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="hist_amount">Original Amount (₱) *</Label>
              <Input
                id="hist_amount"
                type="number"
                value={historicalForm.amount}
                onChange={(e) => setHistoricalForm({ ...historicalForm, amount: e.target.value })}
                placeholder="e.g., 5000"
                min="1"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="hist_reason">Reason/Description *</Label>
              <textarea
                id="hist_reason"
                value={historicalForm.reason}
                onChange={(e) => setHistoricalForm({ ...historicalForm, reason: e.target.value })}
                placeholder="e.g., Emergency loan - March 2025"
                required
                rows="2"
                className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hist_installments">Installments</Label>
                <Select
                  value={historicalForm.installments}
                  onValueChange={(value) => setHistoricalForm({ ...historicalForm, installments: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="6">6</SelectItem>
                    <SelectItem value="12">12</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="hist_paid">Amount Already Paid (₱)</Label>
                <Input
                  id="hist_paid"
                  type="number"
                  value={historicalForm.amount_paid}
                  onChange={(e) => setHistoricalForm({ ...historicalForm, amount_paid: e.target.value })}
                  placeholder="0"
                  min="0"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="hist_date">Original Release Date (optional)</Label>
              <Input
                id="hist_date"
                type="date"
                value={historicalForm.released_at}
                onChange={(e) => setHistoricalForm({ ...historicalForm, released_at: e.target.value })}
                className="mt-1"
              />
            </div>
            {historicalForm.amount && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p className="text-slate-600">
                  <strong>Remaining balance:</strong> {formatCurrency(parseFloat(historicalForm.amount || 0) - parseFloat(historicalForm.amount_paid || 0))}
                </p>
                <p className="text-slate-600">
                  <strong>Per installment:</strong> {formatCurrency(parseFloat(historicalForm.amount || 0) / parseInt(historicalForm.installments || 1))}
                </p>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowHistoricalDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? 'Adding...' : 'Add Historical CA'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Deduction Dialog (Admin) */}
      <Dialog open={showDeductionDialog} onOpenChange={setShowDeductionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record CA Deduction</DialogTitle>
            <DialogDescription>
              {selectedCA && (
                <>
                  Deduct from <strong>{selectedCA.user_name}</strong>'s cash advance<br/>
                  Remaining balance: <strong>{formatCurrency(selectedCA.amount_remaining)}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRecordDeduction} className="space-y-4">
            <div>
              <Label htmlFor="deduction_amount">Deduction Amount (₱) *</Label>
              <Input
                id="deduction_amount"
                type="number"
                value={deductionAmount}
                onChange={(e) => setDeductionAmount(e.target.value)}
                placeholder="e.g., 500"
                min="1"
                max={selectedCA?.amount_remaining || 0}
                required
                className="mt-1"
              />
              {selectedCA && (
                <p className="text-xs text-slate-500 mt-1">
                  Suggested: {formatCurrency(selectedCA.amount_per_installment)} per installment
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="deduction_notes">Notes (optional)</Label>
              <Input
                id="deduction_notes"
                type="text"
                value={deductionNotes}
                onChange={(e) => setDeductionNotes(e.target.value)}
                placeholder="e.g., Jan 1-15 payroll deduction"
                className="mt-1"
              />
            </div>
            {deductionAmount && selectedCA && (
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p className="text-slate-600">
                  <strong>After deduction:</strong> {formatCurrency(selectedCA.amount_remaining - parseFloat(deductionAmount || 0))} remaining
                </p>
                {parseFloat(deductionAmount) >= selectedCA.amount_remaining && (
                  <p className="text-emerald-600 font-medium mt-1">
                    ✓ This will complete the CA (fully paid)
                  </p>
                )}
              </div>
            )}
            <div className="flex gap-2 justify-end pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDeductionDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
                {submitting ? 'Recording...' : 'Record Deduction'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog - Shows employee history before approval */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Approve Cash Advance</DialogTitle>
            <DialogDescription>
              {approvalCA && `${approvalCA.user_name} requested ₱${approvalCA.amount?.toLocaleString()}`}
            </DialogDescription>
          </DialogHeader>
          {approvalCA && (
            <div className="space-y-4">
              {/* Employee History Section */}
              {approvalEmployeeHistory && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <History size={16} />
                    Employee CA History
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-600">Total Borrowed:</span>
                      <span className="ml-1 font-medium">{formatCurrency(approvalEmployeeHistory.total_borrowed)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Total Paid:</span>
                      <span className="ml-1 font-medium text-emerald-600">{formatCurrency(approvalEmployeeHistory.total_paid)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Outstanding:</span>
                      <span className="ml-1 font-medium text-amber-600">{formatCurrency(approvalEmployeeHistory.total_outstanding)}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Pending:</span>
                      <span className="ml-1 font-medium text-blue-600">{formatCurrency(approvalEmployeeHistory.pending_requests)}</span>
                    </div>
                  </div>
                  {approvalEmployeeHistory.history && approvalEmployeeHistory.history.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-amber-200">
                      <p className="text-xs text-slate-600 mb-1">Recent History:</p>
                      <div className="max-h-24 overflow-y-auto space-y-1">
                        {approvalEmployeeHistory.history.slice(0, 5).map((h, idx) => (
                          <div key={idx} className="text-xs flex justify-between">
                            <span className="truncate max-w-[180px]">{h.reason}</span>
                            <span className={`font-medium ${h.status === 'completed' ? 'text-slate-500' : 'text-amber-600'}`}>
                              {formatCurrency(h.amount)} - {h.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><strong>Reason:</strong> {approvalCA.reason}</p>
                {approvalCA.date_required && (
                  <p><strong>Date Required:</strong> {formatDateShort(approvalCA.date_required)}</p>
                )}
                <p><strong>Installments:</strong> {approvalCA.installments} cutoffs</p>
              </div>
              
              <div>
                <Label>Approved Amount (₱)</Label>
                <Input
                  type="number"
                  value={approvalAmount}
                  onChange={(e) => setApprovalAmount(e.target.value)}
                  placeholder="Enter amount to approve"
                  min="1"
                  max={approvalCA.amount}
                />
                {parseFloat(approvalAmount) !== approvalCA.amount && parseFloat(approvalAmount) > 0 && (
                  <p className="text-sm text-amber-600 mt-1">
                    Partial approval: ₱{parseFloat(approvalAmount).toLocaleString()} of ₱{approvalCA.amount.toLocaleString()}
                  </p>
                )}
              </div>
              
              <div>
                <Label>Note/Reason (optional)</Label>
                <textarea
                  value={approvalReason}
                  onChange={(e) => setApprovalReason(e.target.value)}
                  placeholder="Add a note (required if partial approval)"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none"
                  rows={2}
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={submitApproval} className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle size={16} className="mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={showRejectionDialog} onOpenChange={setShowRejectionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Cash Advance</DialogTitle>
            <DialogDescription>
              {approvalCA && `${approvalCA.user_name} requested ₱${approvalCA.amount?.toLocaleString()}`}
            </DialogDescription>
          </DialogHeader>
          {approvalCA && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><strong>Reason:</strong> {approvalCA.reason}</p>
              </div>
              
              <div>
                <Label>Rejection Reason <span className="text-red-500">*</span></Label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none"
                  rows={3}
                  required
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowRejectionDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={submitRejection} variant="destructive">
                  <XCircle size={16} className="mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Employee History Dialog */}
      <Dialog open={showEmployeeHistoryDialog} onOpenChange={setShowEmployeeHistoryDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {employeeHistory?.user_name}'s Cash Advance History
            </DialogTitle>
            <DialogDescription>
              Complete cash advance records for this employee
            </DialogDescription>
          </DialogHeader>
          {employeeHistoryLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : employeeHistory && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-600">Total Borrowed</p>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(employeeHistory.total_borrowed)}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-xs text-emerald-600">Total Paid</p>
                  <p className="text-lg font-bold text-emerald-700">{formatCurrency(employeeHistory.total_paid)}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-600">Outstanding</p>
                  <p className="text-lg font-bold text-amber-700">{formatCurrency(employeeHistory.total_outstanding)}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-600">Pending</p>
                  <p className="text-lg font-bold text-slate-700">{formatCurrency(employeeHistory.pending_requests)}</p>
                </div>
              </div>

              {/* History Table */}
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Date</th>
                      <th className="text-left px-3 py-2 font-semibold">Reason</th>
                      <th className="text-right px-3 py-2 font-semibold">Amount</th>
                      <th className="text-right px-3 py-2 font-semibold">Paid</th>
                      <th className="text-right px-3 py-2 font-semibold">Balance</th>
                      <th className="text-center px-3 py-2 font-semibold">Status</th>
                      <th className="text-center px-3 py-2 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {employeeHistory.history && employeeHistory.history.length > 0 ? (
                      employeeHistory.history.map((ca, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-slate-600">{formatDateShort(ca.requested_at)}</td>
                          <td className="px-3 py-2 text-slate-800 max-w-[150px] truncate" title={ca.reason}>
                            {ca.reason}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(ca.amount)}</td>
                          <td className="px-3 py-2 text-right text-emerald-600">{formatCurrency(ca.amount_paid || 0)}</td>
                          <td className="px-3 py-2 text-right text-amber-600">{formatCurrency(ca.amount_remaining || 0)}</td>
                          <td className="px-3 py-2 text-center">{getStatusBadge(ca.status)}</td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => openEditDialog(ca)}
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteCA(ca.id, employeeHistory.user_name)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                          No cash advance history
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowEmployeeHistoryDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Cash Advance Data</DialogTitle>
            <DialogDescription>
              Import cash advance records from an Excel file
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>📋 Instructions:</strong>
              <ol className="list-decimal list-inside mt-1 space-y-1">
                <li>Download the template below</li>
                <li>Fill in the employee names and CA details</li>
                <li>Upload the completed Excel file</li>
              </ol>
            </div>
            
            <div>
              <Button 
                variant="outline" 
                onClick={handleDownloadTemplate}
                className="w-full"
              >
                <Download size={16} className="mr-2" />
                Download Import Template
              </Button>
            </div>
            
            <div>
              <Label>Select Excel File</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="mt-2 block w-full text-sm text-slate-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-green-50 file:text-green-700
                  hover:file:bg-green-100
                  cursor-pointer"
                disabled={importing}
              />
              <p className="text-xs text-slate-500 mt-1">
                Upload .xlsx or .xls file with cash advance data
              </p>
            </div>
            
            {importing && (
              <div className="flex items-center gap-2 text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Importing...</span>
              </div>
            )}
            
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowImportDialog(false)} disabled={importing}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Cash Advance Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Cash Advance</DialogTitle>
            <DialogDescription>
              Update the cash advance details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-amount">Amount (₱)</Label>
              <Input
                id="edit-amount"
                type="number"
                value={editFormData.amount}
                onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})}
                placeholder="Enter amount"
              />
            </div>
            <div>
              <Label htmlFor="edit-reason">Reason</Label>
              <Input
                id="edit-reason"
                value={editFormData.reason}
                onChange={(e) => setEditFormData({...editFormData, reason: e.target.value})}
                placeholder="Enter reason"
              />
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <select
                id="edit-status"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                value={editFormData.status}
                onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="released">Released</option>
                <option value="partially_paid">Partially Paid</option>
                <option value="paid">Fully Paid</option>
              </select>
            </div>
            <div>
              <Label htmlFor="edit-amount-paid">Amount Paid (₱)</Label>
              <Input
                id="edit-amount-paid"
                type="number"
                value={editFormData.amount_paid}
                onChange={(e) => setEditFormData({...editFormData, amount_paid: e.target.value})}
                placeholder="Enter amount paid"
              />
            </div>
            <div>
              <Label htmlFor="edit-remarks">Remarks</Label>
              <Input
                id="edit-remarks"
                value={editFormData.remarks}
                onChange={(e) => setEditFormData({...editFormData, remarks: e.target.value})}
                placeholder="Enter remarks (optional)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashAdvance;
